import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { ApiRequestError, getMe, logout, type AdminMe } from "@admin/lib/api";
import { AdminShell } from "@admin/components/AdminShell";
import Login from "@admin/pages/Login";
import OverviewPage from "@admin/pages/Overview";
import ContactPage from "@admin/pages/Contact";
import ContactDetailPage from "@admin/pages/ContactDetail";
import NewsletterPage from "@admin/pages/Newsletter";
import UsersPage from "@admin/pages/Users";
import UserDetailPage from "@admin/pages/UserDetail";
import SpendPage from "@admin/pages/Spend";
import OpsPage from "@admin/pages/Ops";
import IncidentsPage from "@admin/pages/Incidents";
import SlackQueuePage from "@admin/pages/SlackQueue";
import MysqlTablesPage from "@admin/pages/MysqlTables";
import ConversationsPage from "@admin/pages/Conversations";
import ConversationDetailPage from "@admin/pages/ConversationDetail";
import HireCodesPage from "@admin/pages/HireCodes";
import HireCodeDetailPage from "@admin/pages/HireCodeDetail";
import HireRewardsPage from "@admin/pages/HireRewards";
import RevenuePage from "@admin/pages/Revenue";
import HibernationPage from "@admin/pages/Hibernation";

type AuthState =
  | { status: "loading" }
  | { status: "anon" }
  | { status: "authed"; me: AdminMe };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    getMe()
      .then((me) => setAuth({ status: "authed", me }))
      .catch((e) => {
        if (e instanceof ApiRequestError && e.status === 401) {
          setAuth({ status: "anon" });
          return;
        }
        setAuth({ status: "anon" });
      });
  }, []);

  const onLogout = async () => {
    try {
      await logout();
    } finally {
      setAuth({ status: "anon" });
    }
  };

  if (auth.status === "loading") {
    return (
      <div className="flex min-h-full items-center justify-center text-muted-fg">Loading...</div>
    );
  }

  if (auth.status === "anon") {
    return (
      <Login
        onAuthed={(me) => {
          setAuth({ status: "authed", me });
        }}
      />
    );
  }

  return (
    <Routes>
      <Route
        element={<AdminShell me={auth.me} onLogout={() => void onLogout()} />}
      >
        <Route index element={<OverviewPage />} />
        <Route path="contact" element={<ContactPage />} />
        <Route path="contact/:id" element={<ContactDetailPage />} />
        <Route path="newsletter" element={<NewsletterPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="hibernation" element={<HibernationPage />} />
        <Route path="hire/codes" element={<HireCodesPage />} />
        <Route path="hire/codes/:id" element={<HireCodeDetailPage />} />
        <Route path="hire/rewards" element={<HireRewardsPage />} />
        <Route path="revenue" element={<RevenuePage />} />
        <Route path="spend" element={<SpendPage />} />
        <Route path="conversations" element={<ConversationsPage />} />
        <Route path="conversations/:sessionId" element={<ConversationDetailPage />} />
        <Route path="reconciliation" element={<OpsPage />} />
        <Route path="incidents" element={<IncidentsPage />} />
        <Route path="slack-queue" element={<SlackQueuePage />} />
        <Route path="mysql-tables" element={<MysqlTablesPage />} />
        <Route path="ops" element={<Navigate to="/reconciliation" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
